import React, { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

// Convert centipawn evaluation to win percentage using a logistic function
const cpToWinPercentage = (cp) => {
    // Formula: 50 + 50 * (2 / (1 + exp(-0.00368208 * cp)) - 1)
    // This is the Lichess formula for win percentage
    return 50 + 50 * (2 / (1 + Math.exp(-0.00368208 * cp)) - 1);
};

const EvaluationBar = ({ evaluation, isAnalyzing, orientation = 'white', analysisDepth = 0 }) => {
    const { percentage, displayText, color, winPercentage } = useMemo(() => {
        if (!evaluation) {
            return { percentage: 50, displayText: '0.00', color: '#888', winPercentage: 50 };
        }

        let evalValue;
        let winPct;

        if (evaluation.type === 'mate') {
            const mateIn = evaluation.value;
            if (mateIn > 0) {
                winPct = 100;
                return {
                    percentage: 100,
                    displayText: `M${mateIn}`,
                    color: '#fff',
                    winPercentage: 100
                };
            } else {
                winPct = 0;
                return {
                    percentage: 0,
                    displayText: `M${Math.abs(mateIn)}`,
                    color: '#000',
                    winPercentage: 0
                };
            }
        } else {
            evalValue = evaluation.value;

            // Calculate win percentage
            const centipawns = evalValue * 100;
            winPct = cpToWinPercentage(centipawns);

            // Cap evaluation at �10 for display purposes
            const cappedEval = Math.max(-10, Math.min(10, evalValue));
            const whitePercentage = ((cappedEval + 10) / 20) * 100;

            const displayValue = evalValue > 0 ? `+${evaluation.displayValue}` : `-${evaluation.displayValue}`;

            return {
                percentage: whitePercentage,
                displayText: displayValue,
                color: evalValue > 0 ? '#fff' : '#000',
                winPercentage: winPct
            };
        }
    }, [evaluation]);

    const displayPercentage = orientation === 'black' ? 100 - percentage : percentage;
    const bottomColor = orientation === 'white' ? '#fff' : '#000';
    const topColor = orientation === 'white' ? '#000' : '#fff';

    // Format nodes display
    const nodesDisplay = evaluation?.nodes ? 
        evaluation.nodes > 1000000 ? `${(evaluation.nodes / 1000000).toFixed(1)}M` : 
        evaluation.nodes > 1000 ? `${(evaluation.nodes / 1000).toFixed(1)}K` : 
        evaluation.nodes : '0';

    return (
        <Box sx={{ width: '60px', height: '100%', minHeight: '400px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
            <Tooltip 
                title={
                    <Box>
                        <Typography variant="caption">Depth: {analysisDepth || evaluation?.depth || 0}</Typography>
                        <br />
                        <Typography variant="caption">Win%: {winPercentage.toFixed(1)}%</Typography>
                        <br />
                        <Typography variant="caption">Nodes: {nodesDisplay}</Typography>
                        {evaluation?.nps && (
                            <>
                                <br />
                                <Typography variant="caption">Speed: {(evaluation.nps / 1000).toFixed(0)}k nps</Typography>
                            </>
                        )}
                    </Box>
                } 
                placement="left"
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#333',
                        position: 'relative',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        border: '1px solid #555'
                    }}
                >
                    {/* Bottom section */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: `${displayPercentage}%`,
                            backgroundColor: bottomColor,
                            transition: 'height 0.3s ease-in-out',
                            borderTop: `1px solid ${bottomColor === '#fff' ? '#000' : '#fff'}`
                        }}
                    />

                    {/* Top section */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: `${100 - displayPercentage}%`,
                            backgroundColor: topColor,
                            transition: 'height 0.3s ease-in-out'
                        }}
                    />

                    {/* Center line */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: 0,
                            right: 0,
                            height: '2px',
                            backgroundColor: '#666',
                            transform: 'translateY(-50%)',
                            zIndex: 1
                        }}
                    />

                    {/* Evaluation text */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 2,
                            backgroundColor: color === '#fff' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                            color: color === '#fff' ? '#000' : '#fff',
                            padding: '4px 6px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            border: '1px solid ' + (color === '#fff' ? '#000' : '#fff'),
                            minWidth: '40px',
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 'bold' }}>
                            {displayText}
                        </Typography>
                    </Box>

                    {/* Analyzing indicator */}
                    {isAnalyzing && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: '5px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 3
                            }}
                        >
                            <Box
                                sx={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: '#4caf50',
                                    animation: 'pulse 1.5s infinite'
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Tooltip>

            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.3; }
                    }
                `}
            </style>
        </Box>
    );
};

export default EvaluationBar;